/**
 * SIL-1351 - the buyer's My Activity surface. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.6;
 * layout and copy from docs/exchange/design-handoff/README.md section 7.
 *
 * ## Why one component renders at two routes
 *
 * The handoff has a single "My Activity" screen. This repo splits its data
 * across two existing routes - `/offers` (offers) and `/transactions`
 * (orders) - and section 4.6 names both files for exactly that reason. Rather
 * than fork the screen, both routes render THIS component for a buyer, so a
 * buyer arriving from either nav entry sees the same activity surface with
 * the same two cards. Collapsing the two nav entries into one is a nav
 * change, which belongs to the rollout (spec section 8), not here.
 *
 * Seller branches of both routes are untouched.
 *
 * ## Scoping
 *
 * Neither list is filtered by comparing against `useAuth().orgId`. That is
 * the PROFILE org and differs from the `customer_org` that owns an offer -
 * the trap `offers.tsx` documents at its `columns()` definition. Offers use
 * the server-computed `isOwnOffer`; orders use whatever
 * `GET /api/client/transactions` returns, which the route has already scoped
 * to the caller's org(s) under RLS - the same scoping today's Transactions
 * page relies on.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { OfferStatusBadge } from '@siliconx/shared/ui/OfferStatusBadge';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import type { Offer } from '@siliconx/shared/dto/offer';
import { api, throwIfNotOk } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import type { OffersResponse } from '../../../lib/types';
import { ActivityCard, type ActivityRowData } from './ActivityCard';
import { CounteredBadge, CounteredOfferActions } from './CounteredOffer';
import { BuyerDepositArea } from '../offers/DepositArea';
import { ExpressedInterestCard } from './ExpressedInterestCard';
import {
  MY_ACTIVITY_SUBTITLE,
  MY_ACTIVITY_TITLE,
  OFFERS_CARD,
  ORDERS_CARD,
  orderStatusLabel,
} from '../copy/activity';

/**
 * Slice 4 (SIL-1319) ships the expressed-interest signal and flips this to
 * `true`. Until then the card is not rendered AND no query for interest data
 * is declared - a hidden card that still fetched would be a request for data
 * no route serves.
 */
const EXPRESSED_INTEREST_ENABLED: boolean = false;

// ---------------------------------------------------------------------------
// Orders - GET /api/client/transactions
// ---------------------------------------------------------------------------

async function fetchOrders() {
  const res = await api.api.client.transactions.$get();
  await throwIfNotOk(res);
  const { data } = await res.json();
  return data;
}

type OrderRow = Awaited<ReturnType<typeof fetchOrders>>[number];

const ORDER_TONES: Record<string, PillTone> = {
  pending_seller_approval: 'amber',
  seller_approved: 'green',
  completed: 'green',
  cancelled: 'neutral',
};

/**
 * SIL-1427: a `kind='buy_now'` offer IS a purchase request — the Orders card
 * shows it (and its status, including the new 'expired' state migration 972
 * added) alongside completed `marketplace_transaction` rows, rather than
 * only in the Offers card next to negotiated price proposals it is not one
 * of. `toOfferRows` below excludes these by the same `kind` check, so a
 * buy_now request appears exactly once, never in both cards.
 */
function toBuyNowOrderRows(offers: Offer[]): ActivityRowData[] {
  return offers
    .filter((o) => o.kind === 'buy_now')
    .map((offer) => {
      const label = offer.displayTitle ?? offer.listingId.slice(0, 8);
      return {
        key: offer.id,
        displayId: offer.id.slice(0, 8),
        title: offer.listingViewable ? (
          <Link
            to="/listings/$listingId"
            params={{ listingId: offer.listingId }}
            className="hover:underline"
          >
            {label}
          </Link>
        ) : (
          label
        ),
        meta: offer.conditionBand ?? offer.listingId.slice(0, 8),
        amountUsd: offer.amountUsd,
        status: <OfferStatusBadge value={offer.status} />,
        date: offer.createdAt,
      };
    });
}

/**
 * An order links to its own transaction detail, not to a listing: a closed
 * `marketplace_transaction` carries `asset_id`/`sku_id` but no `listing_id`,
 * so "link to the listing" has no truthful target here. `/transactions/$id`
 * is the row's detail view and already exists.
 */
function toOrderRows(orders: OrderRow[]): ActivityRowData[] {
  return orders.map((order) => {
    const label = order.skuShortDescription ?? order.skuId ?? order.id.slice(0, 8);
    const price = Number(order.salePriceUsd);
    return {
      key: order.id,
      displayId: order.id.slice(0, 8),
      title: (
        <Link
          to="/transactions/$transactionId"
          params={{ transactionId: order.id }}
          className="hover:underline"
        >
          {label}
        </Link>
      ),
      meta: order.conditionBand ?? '—',
      amountUsd: Number.isFinite(price) ? price : null,
      status: (
        <StatusPill tone={ORDER_TONES[order.lifecycleState] ?? 'neutral'}>
          {orderStatusLabel(order.lifecycleState)}
        </StatusPill>
      ),
      date: order.closedAt !== '' ? order.closedAt : null,
    };
  });
}

// ---------------------------------------------------------------------------
// Offers - GET /api/client/offers, restricted to the viewer's own offers
// ---------------------------------------------------------------------------

async function fetchOffers(): Promise<OffersResponse> {
  const res = await api.api.client.offers.$get();
  await throwIfNotOk(res);
  return (await res.json()) as unknown as OffersResponse;
}

/**
 * A countered offer is the one row on this page with a decision to make: the
 * warn badge carries the counter amount and the two actions sit beside it.
 * The amount column keeps showing the buyer's OWN offer, so the two figures
 * are never confused for each other.
 */
export function toOfferRows(offers: Offer[]): ActivityRowData[] {
  // SIL-1427: buy_now offers render in the Orders card (toBuyNowOrderRows)
  // instead — this card is negotiated price proposals only.
  return offers
    .filter((offer) => offer.kind !== 'buy_now')
    .map((offer) => {
    const label = offer.displayTitle ?? offer.listingId.slice(0, 8);
    const countered = offer.status === 'countered' && offer.counterAmountUsd != null;
    return {
      key: offer.id,
      displayId: offer.id.slice(0, 8),
      title: offer.listingViewable ? (
        <Link
          to="/listings/$listingId"
          params={{ listingId: offer.listingId }}
          className="hover:underline"
        >
          {label}
        </Link>
      ) : (
        label
      ),
      meta: offer.conditionBand ?? offer.listingId.slice(0, 8),
      amountUsd: offer.amountUsd,
      status: countered ? (
        <CounteredBadge counterAmountUsd={offer.counterAmountUsd as number} />
      ) : (
        <OfferStatusBadge
          value={offer.status}
          refusalReason={offer.refusalReason}
          cancellationReason={offer.cancellationReason}
        />
      ),
      actions: countered ? <CounteredOfferActions offer={offer} /> : undefined,
      // SIL-2218: a buyer-only org pays here (no seller table). `offers` is
      // already restricted to isOwnOffer by the caller; the API is the gate.
      detail:
        offer.status === 'accepted' || offer.status === 'deposit_held' ? (
          <BuyerDepositArea offer={offer} />
        ) : undefined,
      date: offer.createdAt,
    };
  });
}

export function MyActivity({
  /**
   * The host route's own nav path, so SIL-364's Pin toggle keeps working.
   * It is a prop, not a constant, precisely because this component renders
   * at two routes - hardcoding either one would pin the wrong page from the
   * other.
   */
  pinTo,
}: {
  pinTo?: string;
} = {}) {
  const ordersQ = useQuery({ queryKey: qk.transactions.all, queryFn: fetchOrders });
  const offersQ = useQuery({ queryKey: qk.offers(), queryFn: fetchOffers });

  const ownOffers = (offersQ.data?.data ?? []).filter((o) => o.isOwnOffer);
  const orderRows = [...toOrderRows(ordersQ.data ?? []), ...toBuyNowOrderRows(ownOffers)];
  const offerRows = toOfferRows(ownOffers);

  return (
    <div className="space-y-5">
      <PageHeader title={MY_ACTIVITY_TITLE} subtitle={MY_ACTIVITY_SUBTITLE} pinTo={pinTo} />
      <ActivityCard copy={ORDERS_CARD} rows={orderRows} isLoading={ordersQ.isLoading} />
      <ActivityCard copy={OFFERS_CARD} rows={offerRows} isLoading={offersQ.isLoading} />
      {/*
        Expressed interest (spec section 4.6): hidden until SIL-1319. The card
        is built and wired; slice 4 flips `EXPRESSED_INTEREST_ENABLED` and
        swaps the empty literal for its query. Keeping the JSX here rather
        than a TODO means the flip is one line and cannot miss a prop.
      */}
      {EXPRESSED_INTEREST_ENABLED && <ExpressedInterestCard rows={[]} />}
    </div>
  );
}
